import { Controller } from "@hotwired/stimulus";

/**
 * Slide content horizontally
 * @example <caption>HTML, controller registered as "expand"</caption>
 * <div data-controller="slider" data-slider-js-class-value="js">
 *   <div data-slider-target="slider-body">
 *     <div data-slider-target="slide">Slide 1</div>
 *     <div data-slider-target="slide">Slide 2</div>
 *   </div>
 *   <div>
 *     <button data-action="slider#selectSlide" type="button">
 *		   Slide 1
 *     </button>
 *     <button data-action="slider#selectSlide" type="button">
 *		   Slide 2
 *	   </button>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    "sliderBody",
    "ariaLiveRegion",
    "slideTrigger",
    "slide",
    "forwardBackwardActions",
    "previousTrigger",
    "nextTrigger",
  ];
  static classes = ["js"];
  static values = {
    liveregion: String,
  };

  /**
   * Index of last visible slide
   * @member
   */
  lastIndex = 0;

  /**
   * Number of current visible slide
   * @member
   */
  number = 0;

  /**
   * Sliding timer
   * @member
   */
  slidingTimer = null;

  /** @type {number} */
  scrollDebounceTimeout = null;

  connect() {
    this.element.classList.add(this.jsClass || "js");

    this.initEventListeners();
    this.slideTo(0, false, false);
  }

  initEventListeners() {
    this.slideTriggerTargets[0].classList.add("active");

    this.slideTriggerTargets.forEach((elem, key) =>
      elem.addEventListener("click", () => this.slideTo(key))
    );

    this.sliderBodyTarget.addEventListener("scroll", () => {
      if (this.scrollDebounceTimeout) {
        clearTimeout(this.scrollDebounceTimeout);
      }

      this.scrollDebounceTimeout = setTimeout(() => {
        let index = Math.round(
          this.sliderBodyTarget.scrollLeft / this.element.offsetWidth
        );

        this.number =
          index > this.lastIndex ? Math.ceil(index) : Math.floor(index);

        [...this.slideTriggerTargets].map((item) =>
          item.classList.remove("active")
        );
        this.slideTriggerTargets[this.number].classList.add("active");
        if (Number.isInteger(index)) this.lastIndex = index;
      }, 50);
    });
  }

  /**
   * Event handler for click element of a specific slide.
   * Slides to the specific slide.
   * @param {Event} event Event object
   */
  selectSlide(event) {
    this.slideTargets.forEach((item, i) => {
      if (item === event.currentTarget) {
        this.slideTo(i);
      }
    });
  }

  /**
   * Event handler to go to previous slide
   * @param {Event} event Event object
   */
  selectPreviousSlide(event) {
    this.slideTo(this.number - 1);
  }

  /**
   * Event handler to go to next slide
   * @param {Event} event Event object
   */
  selectNextSlide(event) {
    this.slideTo(this.number + 1);
  }

  /**
   * Set slide of content slider
   * @param  {number} index Index to slide to
   * @param  {boolean} [liveRegionUpdate=true] Whether to trigger notification in live region. Set to false for initialization
   */
  slideTo(index, liveRegionUpdate = true, focus = true) {
    this.sliderBodyTarget.scroll({
      top: 0,
      left: this.element.offsetWidth * index,
      behavior: "smooth",
    });

    const template = this.liveregionValue.replace("$x", index + 1);

    this.slideTargets.forEach((slide, i) => {
      if (i !== index) {
        slide.setAttribute("aria-hidden", true);
        slide.removeAttribute("tabindex");
      } else {
        slide.setAttribute("aria-hidden", false);
        slide.setAttribute("tabindex", -1);

        if (focus) {
          this.slidingTimer = setTimeout(() => slide.focus(), 1000);
        }
      }
    });

    if (liveRegionUpdate && this.ariaLiveRegionTarget) {
      this.ariaLiveRegionTarget.innerHTML = `${template}`;
    }

    if (index <= 0) {
      this.previousTriggerTarget.setAttribute("hidden", "");
    } else {
      this.previousTriggerTarget.removeAttribute("hidden");
    }

    if (index === this.slideTargets.length - 1) {
      this.nextTriggerTarget.setAttribute("hidden", "");
    } else {
      this.nextTriggerTarget.removeAttribute("hidden");
    }
  }
}
